import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ScanLine, X } from "lucide-react";
import { blockById, setBlockSources } from "../../store/state.js";
import { showToast } from "../../utils/dom.js";
import { chunkCount, firstExcerpt } from "./DocumentTable";
import EmptyState from "../common/EmptyState";
import { t, tc } from "../../i18n/index.js";
import type { KbDocument } from "../../types/index.js";

// Opened by the report drawer, which is still rendered imperatively: it
// dispatches `sfcr:manage-sources` rather than mounting React inside itself,
// because the drawer rebuilds its whole DOM on every render.
export const MANAGE_SOURCES_EVENT = "sfcr:manage-sources";

export default function SourcePickerDialog({ documents, onSaved }: {
  documents: KbDocument[]; onSaved: () => void;
}) {
  const [blockId, setBlockId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const open = (event: Event) => {
      const id = (event as CustomEvent<{ blockId: string }>).detail?.blockId;
      const block = id && blockById(id);
      if (!block) return;
      setBlockId(id);
      setSelected([...(block.sourceDocIds || [])]);
      setQuery("");
    };
    window.addEventListener(MANAGE_SOURCES_EVENT, open);
    return () => window.removeEventListener(MANAGE_SOURCES_EVENT, open);
  }, []);

  useEffect(() => {
    if (!blockId) return;
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") close(event); };
    document.addEventListener("keydown", escape, true);
    dialog.current?.querySelector<HTMLElement>("input, button")?.focus();
    return () => document.removeEventListener("keydown", escape, true);
  }, [blockId]);

  if (!blockId) return null;
  const block = blockById(blockId);

  function close(event?: Event) {
    event?.stopPropagation();
    setBlockId(null);
  }

  function toggle(id: string) {
    setSelected(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  }

  function save() {
    setBlockSources(blockId, selected);
    showToast(t("toast.sourcesSaved", { count: selected.length, code: block.code }));
    setBlockId(null);
    onSaved();
  }

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? documents.filter(document => `${document.name} ${document.category} ${document.origin}`.toLowerCase().includes(needle))
    : documents;

  return <div className="picker-root" role="presentation" onClick={() => close()}>
    <div className="picker" role="dialog" aria-modal="true" aria-label={t("sources.manage.title", { code: block.code })}
      ref={dialog} onClick={event => event.stopPropagation()}>
      <header>
        <div>
          <h2>{t("sources.manage.title", { code: block.code })}</h2>
          <p>{t("sources.manage.subtitle")}</p>
        </div>
        <button className="icon-btn" onClick={() => close()} aria-label={t("drawer.close")}><X size={16} /></button>
      </header>

      <input className="picker-search" type="search" value={query} placeholder={t("sources.manage.search")}
        onChange={event => setQuery(event.target.value)} aria-label={t("sources.manage.search")} />

      <div className="picker-list">
        {visible.length ? visible.map(document => {
          const checked = selected.includes(document.id);
          return <label key={document.id} className={`picker-item ${checked ? "checked" : ""}`}>
            <input type="checkbox" checked={checked} onChange={() => toggle(document.id)} />
            <span className="picker-item-body">
              <strong>{document.name}</strong>
              <em>{firstExcerpt(document, 140)}</em>
              <span className="picker-item-meta">
                <span className="kb-pill">{tc(`docCategory.${document.category}`, document.category)}</span>
                <span className="mini-pill">{t("sources.chunks", { count: chunkCount(document) })}</span>
                {document.status !== "indexed" && <span className="ko-pill"><ScanLine size={11} aria-hidden="true" />{tc(`docStatus.${document.status}`, document.status)}</span>}
                {document.exceedsDraftLimit && <span className="warn-pill"><AlertTriangle size={11} aria-hidden="true" />{t("admin.kb.overDraftLimit")}</span>}
              </span>
            </span>
          </label>;
        }) : <EmptyState title={t("sources.manage.noMatch")} />}
      </div>

      <footer>
        <span className="muted">{t("sources.manage.selected", { count: selected.length })}</span>
        <span>
          <button className="btn outline" onClick={() => close()}>{t("action.cancel")}</button>
          <button className="btn dark" onClick={save}>{t("action.save")}</button>
        </span>
      </footer>
    </div>
  </div>;
}
